import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

interface PaymentMethodModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectPaymentMethod?: (method: 'cash' | 'network' | 'partial') => void
  showPartialPayment?: boolean
  isCustomerService?: boolean
}

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSelectPaymentMethod,
  showPartialPayment = true,
  isCustomerService = false
}) => {
  const { t, i18n } = useTranslation()
  const { settings } = useSelector((state: RootState) => state.settings)
  const isRTL = settings.language === 'ar'
  
  // Ensure i18n language matches settings
  useEffect(() => {
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language)
    }
  }, [settings.language, i18n])

  if (!isOpen) return null

  const handlePaymentMethodClick = (method: 'cash' | 'network' | 'partial') => {
    if (onSelectPaymentMethod) {
      onSelectPaymentMethod(method)
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative z-10 w-[480px] bg-white rounded-2xl flex flex-col justify-start items-center overflow-hidden">
          {/* Header */}
          <div className="self-stretch pl-4 pr-6 py-4 bg-white inline-flex justify-start items-center gap-6 overflow-hidden">
           

            {/* Title */}
            <div className="flex-1 text-right justify-start text-text-primary text-xl font-medium font-['IBM Plex Sans Arabic'] leading-7">
              {t('payment.select_payment_method')}
            </div>
             {/* Close Button */}
             <button
              onClick={onClose}
              className="size-9 bg-white rounded-xl border border-gray-100 flex justify-center items-center overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <X className="size-5 text-icon-secondary" />
            </button>
          </div>

          {/* Payment Options */}
          <div className="self-stretch px-4 pt-2 pb-4 bg-white flex flex-col justify-center items-start gap-3 overflow-hidden">
            {/* Cash Option */}
            <button
              onClick={() => handlePaymentMethodClick('cash')}
              className="self-stretch h-12 px-4 bg-neutral-100 rounded-xl inline-flex justify-start items-center gap-2 overflow-hidden cursor-pointer hover:bg-neutral-200 transition-colors"
            >
              {/* Cash Icon */}
              <div className="size-5 flex items-center justify-center text-flutter-button">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g id="Frame">
                    <path id="Vector" d="M13.3872 2.29175C14.523 2.29172 15.439 2.29171 16.1597 2.38859C16.9098 2.48944 17.5413 2.70568 18.0429 3.20727C18.5445 3.70887 18.7607 4.34032 18.8616 5.09042C18.9585 5.81117 18.9584 6.72716 18.9584 7.86304V9.63717C18.9584 10.773 18.9585 11.689 18.8616 12.4097C18.767 13.1129 18.5714 13.7122 18.134 14.1977L18.0429 14.2929C17.5413 14.7945 16.9098 15.0107 16.1597 15.1116C15.439 15.2085 14.523 15.2084 13.3872 15.2084H6.61304C5.47716 15.2084 4.56117 15.2085 3.84042 15.1116C3.13722 15.017 2.53792 14.8214 2.05249 14.384L1.95727 14.2929C1.45568 13.7913 1.23944 13.1598 1.13859 12.4097C1.04171 11.689 1.04172 10.773 1.04175 9.63717V7.86304C1.04172 6.72716 1.04171 5.81117 1.13859 5.09042C1.23944 4.34032 1.45568 3.70887 1.95727 3.20727C2.45887 2.70568 3.09032 2.48944 3.84042 2.38859C4.56117 2.29171 5.47716 2.29172 6.61304 2.29175H13.3872ZM2.30884 11.3087C2.32103 11.6656 2.34181 11.9726 2.37802 12.2421C2.46022 12.8535 2.61047 13.1777 2.84107 13.4082L2.93222 13.4913C3.15706 13.6757 3.47227 13.7994 4.00724 13.8713C4.27676 13.9076 4.58376 13.9292 4.94067 13.9413C4.68627 12.6116 3.63849 11.5632 2.30884 11.3087ZM17.6922 11.2957C16.3425 11.5264 15.2773 12.5917 15.0465 13.9413C15.4088 13.9292 15.7202 13.908 15.9929 13.8713C16.6042 13.7891 16.9285 13.6389 17.1591 13.4082L17.2421 13.3172C17.4264 13.0923 17.5502 12.7769 17.6222 12.2421C17.6587 11.9694 17.68 11.6579 17.6922 11.2957ZM10.0001 6.25008C8.61933 6.25008 7.50008 7.36937 7.50008 8.75008C7.50008 10.1308 8.61933 11.2501 10.0001 11.2501C11.3808 11.2501 12.5001 10.1308 12.5001 8.75008C12.5001 7.36937 11.3808 6.25008 10.0001 6.25008ZM4.94067 3.55802C4.5838 3.57022 4.27674 3.59099 4.00724 3.6272C3.39595 3.70938 3.07169 3.85973 2.84107 4.09025C2.61047 4.32086 2.46023 4.64513 2.37802 5.25643C2.34179 5.52598 2.32103 5.8337 2.30884 6.19067C3.63852 5.93614 4.68627 4.88775 4.94067 3.55802ZM15.0595 3.55802C15.3153 4.88317 16.3658 5.92833 17.6913 6.18742C17.6791 5.83167 17.6582 5.52525 17.6222 5.25643C17.5399 4.64511 17.3897 4.32086 17.1591 4.09025C16.9285 3.85967 16.6042 3.70941 15.9929 3.6272C15.7235 3.591 15.4162 3.57022 15.0595 3.55802Z" fill="currentColor"/>
                    <path id="Vector_2" d="M15.8334 16.4583C16.1786 16.4583 16.4584 16.7381 16.4584 17.0833C16.4584 17.4284 16.1786 17.7083 15.8334 17.7083H4.16675C3.82157 17.7083 3.54175 17.4284 3.54175 17.0833C3.54175 16.7381 3.82157 16.4583 4.16675 16.4583H15.8334Z" fill="currentColor"/>
                  </g>
                </svg>
              </div>

              {/* Cash Text */}
              <div className="flex-1 text-right justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                {t('payment.cash')}
              </div>

              {/* Chevron Arrow */}
              {isRTL ? (
                <ChevronLeft className="size-5 text-flutter-button" />
              ) : (
                <ChevronRight className="size-5 text-flutter-button" />
              )}
            </button>

            {/* Network/Card Option */}
            {isCustomerService ? (
              <button
                onClick={() => handlePaymentMethodClick('network')}
                className="self-stretch h-12 px-4 bg-neutral-100 rounded-xl inline-flex justify-start items-center gap-2 overflow-hidden cursor-pointer hover:bg-neutral-200 transition-colors"
              >
                {/* Network Icon */}
                <div className="size-5 flex items-center justify-center text-flutter-button">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="Frame">
                      <path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M6.28582 11.4941C8.27793 9.50198 11.7222 9.50198 13.7143 11.4941C14.0397 11.8195 14.0397 12.3471 13.7143 12.6726C13.3889 12.998 12.8612 12.998 12.5358 12.6726C11.1946 11.3313 8.80558 11.3313 7.46434 12.6726C7.1389 12.998 6.61126 12.998 6.28582 12.6726C5.96039 12.3471 5.96039 11.8195 6.28582 11.4941Z" fill="currentColor"/>
                      <path id="Vector_2" fillRule="evenodd" clipRule="evenodd" d="M4.04987 8.94325C7.68261 5.91594 12.5048 5.89455 15.9688 8.95925C16.3136 9.26425 16.3458 9.79092 16.0408 10.1356C15.7358 10.4803 15.2092 10.5125 14.8645 10.2076C12.0492 7.71673 8.15076 7.69533 5.11684 10.2236C4.76328 10.5183 4.23781 10.4705 3.94317 10.1169C3.64853 9.76333 3.6963 9.23783 4.04987 8.94325Z" fill="currentColor"/>
                      <path id="Vector_3" fillRule="evenodd" clipRule="evenodd" d="M9.99993 2.91675C13.0791 2.91673 16.1148 4.11935 18.8709 6.44662C19.2225 6.74356 19.2669 7.26932 18.9699 7.62096C18.673 7.97261 18.1473 8.01695 17.7956 7.72002C15.2885 5.60296 12.6226 4.58341 9.99993 4.58341C7.37731 4.58342 4.71136 5.603 2.20426 7.72011C1.85261 8.01704 1.32685 7.9727 1.02991 7.62106C0.732975 7.26943 0.777317 6.74366 1.12896 6.44672C3.885 4.11941 6.92081 2.91676 9.99993 2.91675Z" fill="currentColor"/>
                      <path id="Vector_4" fillRule="evenodd" clipRule="evenodd" d="M8.125 15C8.125 13.9645 8.9645 13.125 10 13.125C11.0355 13.125 11.875 13.9645 11.875 15C11.875 16.0355 11.0355 16.875 10 16.875C8.9645 16.875 8.125 16.0355 8.125 15Z" fill="currentColor"/>
                    </g>
                  </svg>
                </div>

                {/* Network Text */}
                <div className="flex-1 text-right justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5 line-clamp-1">
                  {t('payment.online')}
                </div>

                {/* Chevron Arrow */}
                {isRTL ? (
                  <ChevronLeft className="size-5 text-flutter-button" />
                ) : (
                  <ChevronRight className="size-5 text-flutter-button" />
                )}
              </button>
            ) : (
              <button
                onClick={() => handlePaymentMethodClick('network')}
                className="self-stretch h-12 px-4 bg-neutral-100 rounded-xl inline-flex justify-start items-center gap-2 overflow-hidden cursor-pointer hover:bg-neutral-200 transition-colors"
              >
                {/* Network Icon */}
                <div className="size-5 flex items-center justify-center text-flutter-button">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="Frame">
                      <path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M6.28582 11.4941C8.27793 9.50198 11.7222 9.50198 13.7143 11.4941C14.0397 11.8195 14.0397 12.3471 13.7143 12.6726C13.3889 12.998 12.8612 12.998 12.5358 12.6726C11.1946 11.3313 8.80558 11.3313 7.46434 12.6726C7.1389 12.998 6.61126 12.998 6.28582 12.6726C5.96039 12.3471 5.96039 11.8195 6.28582 11.4941Z" fill="currentColor"/>
                      <path id="Vector_2" fillRule="evenodd" clipRule="evenodd" d="M4.04987 8.94325C7.68261 5.91594 12.5048 5.89455 15.9688 8.95925C16.3136 9.26425 16.3458 9.79092 16.0408 10.1356C15.7358 10.4803 15.2092 10.5125 14.8645 10.2076C12.0492 7.71673 8.15076 7.69533 5.11684 10.2236C4.76328 10.5183 4.23781 10.4705 3.94317 10.1169C3.64853 9.76333 3.6963 9.23783 4.04987 8.94325Z" fill="currentColor"/>
                      <path id="Vector_3" fillRule="evenodd" clipRule="evenodd" d="M9.99993 2.91675C13.0791 2.91673 16.1148 4.11935 18.8709 6.44662C19.2225 6.74356 19.2669 7.26932 18.9699 7.62096C18.673 7.97261 18.1473 8.01695 17.7956 7.72002C15.2885 5.60296 12.6226 4.58341 9.99993 4.58341C7.37731 4.58342 4.71136 5.603 2.20426 7.72011C1.85261 8.01704 1.32685 7.9727 1.02991 7.62106C0.732975 7.26943 0.777317 6.74366 1.12896 6.44672C3.885 4.11941 6.92081 2.91676 9.99993 2.91675Z" fill="currentColor"/>
                      <path id="Vector_4" fillRule="evenodd" clipRule="evenodd" d="M8.125 15C8.125 13.9645 8.9645 13.125 10 13.125C11.0355 13.125 11.875 13.9645 11.875 15C11.875 16.0355 11.0355 16.875 10 16.875C8.9645 16.875 8.125 16.0355 8.125 15Z" fill="currentColor"/>
                    </g>
                  </svg>
                </div>

                {/* Network Text */}
                <div className="flex-1 text-right justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5 line-clamp-1">
                  {t('payment.network')}
                </div>

                {/* Chevron Arrow */}
                {isRTL ? (
                  <ChevronLeft className="size-5 text-flutter-button" />
                ) : (
                  <ChevronRight className="size-5 text-flutter-button" />
                )}
              </button>
            )}

            {/* Partial Payment Option */}
            {showPartialPayment && (
              <button
                onClick={() => handlePaymentMethodClick('partial')}
                className="self-stretch h-12 px-4 bg-neutral-100 rounded-xl inline-flex justify-start items-center gap-2 overflow-hidden cursor-pointer hover:bg-neutral-200 transition-colors"
              >
                {/* Partial Payment Icon */}
                <div className="size-5 flex items-center justify-center text-flutter-button">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="Frame" clipPath="url(#clip0_2740_1001)">
                      <path id="Vector" d="M10.0001 18.3334C14.6025 18.3334 18.3334 14.6025 18.3334 10.0001C18.3334 5.39771 14.6025 1.66675 10.0001 1.66675C5.39771 1.66675 1.66675 5.39771 1.66675 10.0001C1.66675 14.6025 5.39771 18.3334 10.0001 18.3334Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path id="Vector_2" d="M10 3.75C11.6576 3.75 13.2473 4.40848 14.4194 5.58058C15.5915 6.75268 16.25 8.34242 16.25 10C16.25 11.6576 15.5915 13.2473 14.4194 14.4194C13.2473 15.5915 11.6576 16.25 10 16.25V3.75Z" fill="currentColor"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_2740_1001">
                        <rect width="20" height="20" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                {/* Partial Payment Text */}
                <div className="flex-1 text-right justify-start text-text-primary text-sm font-medium font-['IBM Plex Sans Arabic'] leading-5">
                  {t('payment.partial_payment')}
                </div>

                {/* Chevron Arrow */}
                {isRTL ? (
                  <ChevronLeft className="size-5 text-flutter-button" />
                ) : (
                  <ChevronRight className="size-5 text-flutter-button" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
  )  
}

export default PaymentMethodModal

